import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GET_LEAVE_TYPES } from '../../graphql/queries';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  department?: string;
}

interface Leave {
  id: string;
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
  reason?: string;
  approvedBy?: string;
  approvedAt?: string;
  employee: Employee;
}

interface LeaveDetailsDialogProps {
  leave: Leave | null;
  open: boolean;
  onClose: () => void;
  onApprove?: (leaveId: string) => Promise<void>;
  onReject?: (leaveId: string) => Promise<void>;
  onDelete?: (leaveId: string) => void;
  loading?: boolean;
}

const FALLBACK_LEAVE_TYPES = [
  { value: 'VACATION', label: 'Urlop wypoczynkowy', color: 'bg-green-100 text-green-800' },
  { value: 'SICK_LEAVE', label: 'Zwolnienie lekarskie', color: 'bg-red-100 text-red-800' },
  { value: 'PERSONAL', label: 'Urlop okolicznościowy', color: 'bg-blue-100 text-blue-800' },
  { value: 'MATERNITY', label: 'Urlop macierzyński', color: 'bg-pink-100 text-pink-800' },
  { value: 'PATERNITY', label: 'Urlop ojcowski', color: 'bg-purple-100 text-purple-800' },
  { value: 'UNPAID', label: 'Urlop bezpłatny', color: 'bg-gray-100 text-gray-800' },
  { value: 'OTHER', label: 'Inny', color: 'bg-yellow-100 text-yellow-800' },
];

const LEAVE_STATUS = [
  { value: 'PENDING', label: 'Oczekujący', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'APPROVED', label: 'Zatwierdzony', color: 'bg-green-100 text-green-800' },
  { value: 'REJECTED', label: 'Odrzucony', color: 'bg-red-100 text-red-800' },
  { value: 'CANCELLED', label: 'Anulowany', color: 'bg-gray-100 text-gray-800' },
];

export const LeaveDetailsDialog: React.FC<LeaveDetailsDialogProps> = ({
  leave,
  open,
  onClose,
  onApprove,
  onReject,
  onDelete,
  loading = false,
}) => {
  const { data: leaveTypesData } = useQuery(GET_LEAVE_TYPES, {
    fetchPolicy: 'cache-first',
  });

  const leaveTypes = React.useMemo(() => {
    const fallbackColorMap: Record<string, string> = {};
    FALLBACK_LEAVE_TYPES.forEach(t => { fallbackColorMap[t.value] = t.color; });

    const dynamicTypes = (leaveTypesData?.leaveTypes || [])
      .filter((t: any) => t.isActive)
      .map((t: any) => ({
        value: t.code,
        label: t.displayName,
        color: fallbackColorMap[t.code] || 'bg-yellow-100 text-yellow-800',
      }));
    return dynamicTypes.length > 0 ? dynamicTypes : FALLBACK_LEAVE_TYPES;
  }, [leaveTypesData]);

  if (!leave) return null;

  const getLeaveTypeConfig = (type: string) => {
    return leaveTypes.find((t: { value: string }) => t.value === type) || { value: type, label: type, color: 'bg-yellow-100 text-yellow-800' };
  };

  const getStatusConfig = (status: string) => {
    return LEAVE_STATUS.find((s) => s.value === status) || LEAVE_STATUS[0];
  };

  const typeConfig = getLeaveTypeConfig(leave.leaveType);
  const statusConfig = getStatusConfig(leave.status);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Szczegóły nieobecności</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pracownik</p>
              <p className="text-base">
                {leave.employee.lastName} {leave.employee.firstName}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Dział</p>
              <p className="text-base">{leave.employee.department || 'Brak'}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Rodzaj</p>
              <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Data początkowa</p>
              <p className="text-base">
                {new Date(leave.startDate).toLocaleDateString('pl-PL')}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Data końcowa</p>
              <p className="text-base">
                {new Date(leave.endDate).toLocaleDateString('pl-PL')}
              </p>
            </div>

            {leave.reason && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground">Powód</p>
                <p className="text-base">{leave.reason}</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Zamknij
          </Button>

          {leave.status === 'PENDING' && (
            <>
              {onReject && (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => onReject(leave.id)}
                  disabled={loading}
                >
                  Odrzuć
                </Button>
              )}
              {onApprove && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => onApprove(leave.id)}
                  disabled={loading}
                >
                  Zatwierdź
                </Button>
              )}
            </>
          )}

          {onDelete && (
            <Button
              variant="outline"
              className="text-red-600 border-red-600 hover:bg-red-50"
              onClick={() => onDelete(leave.id)}
              disabled={loading}
            >
              Usuń
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
